'use client';

import classNames from 'classnames';
import { useMemo } from 'react';
import { LinkInfo } from '../../types';
import IconRenderer, { IconDefinition } from '../IconRenderer';
import LinkButtonOrDiv from '../LinkButtonOrDiv';
import styles from './styles/StatusBadge.module.scss';

export type StatusBadgeProps = {
  className?: string;
  icon?: IconDefinition<'orientation'>;
  variant: 'caution' | 'info' | 'inverse' | 'negative' | 'neutral' | 'positive';
  text: string;
  title?: string;
  onClick?: () => void;
  size: 'sm' | 'md';
  treatment: 'default' | 'minimal' | 'strong';
  linkInfo?: LinkInfo;
};

// https://www.figma.com/design/9Y1n3VoMJAcTGPe82bvySj/Denali-Components?node-id=3559-61646&t=8N0pKlYEr2NhrNzg-4
const StatusBadge = ({ className, icon, variant, text, title, onClick, size, treatment, linkInfo }: StatusBadgeProps) => {
  const iconElement = useMemo(() => {
    if (treatment === 'minimal') {
      return <div className={styles.dot} />;
    }
    return icon ? <IconRenderer icon={icon} defaults={{ color: 'currentColor', size: 'xs' }} /> : null;
  }, [icon, treatment]);

  const content = useMemo(
    () => (
      <>
        {iconElement}
        <span className={styles.textContent} title={title}>
          {text}
        </span>
      </>
    ),
    [iconElement, title, text]
  );

  return (
    <LinkButtonOrDiv
      className={classNames(
        styles.container,
        styles[size],
        styles[variant],
        styles[treatment],
        { [styles.minimal]: treatment === 'minimal' },
        className
      )}
      linkInfo={linkInfo}
      onClick={onClick}
    >
      {content}
    </LinkButtonOrDiv>
  );
};

export default StatusBadge;
